import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import api from '../api';
import { 
  Save, Download, Plus, Trash2, ChevronLeft, RefreshCw, 
  GripVertical, MoreHorizontal, Database, DollarSign, Users,
  Package, ShoppingCart, Megaphone, Calculator, FileSpreadsheet,
  Check, X, Copy, ArrowUp, ArrowDown, Sparkles, Link2, History, RotateCcw
} from 'lucide-react';

const MONTHS = [
  { key: '01', label: 'Jan' },
  { key: '02', label: 'Feb' },
  { key: '03', label: 'Mar' },
  { key: '04', label: 'Apr' },
  { key: '05', label: 'May' },
  { key: '06', label: 'Jun' },
  { key: '07', label: 'Jul' },
  { key: '08', label: 'Aug' },
  { key: '09', label: 'Sep' },
  { key: '10', label: 'Oct' },
  { key: '11', label: 'Nov' },
  { key: '12', label: 'Dec' }
];

const BUDGET_TYPES = [
  { value: 'revenue', label: 'Revenue', color: 'bg-green-100 text-green-800' },
  { value: 'cogs', label: 'COGS', color: 'bg-orange-100 text-orange-800' },
  { value: 'opex', label: 'OpEx', color: 'bg-blue-100 text-blue-800' },
  { value: 'capex', label: 'CapEx', color: 'bg-purple-100 text-purple-800' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-800' }
];

const BudgetBuilderPage = () => {
  const { budgetId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [budget, setBudget] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [crmData, setCrmData] = useState(null);
  const [showCrmDataDialog, setShowCrmDataDialog] = useState(false);
  const [showAddRowDialog, setShowAddRowDialog] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const inputRef = useRef(null);

  // Version History
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [savingVersion, setSavingVersion] = useState(false);
  const [newVersionNotes, setNewVersionNotes] = useState('');

  const [newRow, setNewRow] = useState({
    category: '',
    subcategory: '',
    description: '',
    account_code: '',
    budget_type: 'opex',
    notes: ''
  });

  const fiscalYear = budget?.fiscal_year || new Date().getFullYear();

  // Fetch budget data
  const fetchBudget = useCallback(async () => {
    if (!budgetId) return;
    try {
      setLoading(true);
      const res = await api.get(`/finance/budget-templates/${budgetId}`);
      setBudget(res.data);
      
      // Initialize line items with monthly amounts
      const items = (res.data.line_items || []).map((item, idx) => ({
        ...item,
        id: item.id || `row-${idx}`,
        monthly_amounts: item.monthly_amounts || {}
      }));
      setLineItems(items);
    } catch (error) {
      toast.error('Failed to load budget');
      navigate('/finance');
    } finally {
      setLoading(false);
    }
  }, [budgetId, navigate]);

  // Fetch CRM data for integration
  const fetchCrmData = useCallback(async () => {
    try {
      const [dealsRes, productsRes, ordersRes, contactsRes, campaignsRes] = await Promise.all([
        api.get('/deals'),
        api.get('/products'),
        api.get('/orders'),
        api.get('/contacts'),
        api.get('/marketing/campaigns')
      ]);

      // Calculate summaries
      const deals = dealsRes.data || [];
      const products = productsRes.data || [];
      const orders = ordersRes.data || [];
      const contacts = contactsRes.data || [];
      const campaigns = campaignsRes.data || [];

      const pipelineValue = deals
        .filter(d => !['closed_won', 'closed_lost'].includes(d.stage))
        .reduce((sum, d) => sum + (d.value || 0), 0);

      const wonDealsValue = deals
        .filter(d => d.stage === 'closed_won')
        .reduce((sum, d) => sum + (d.value || 0), 0);

      const avgDealSize = deals.length > 0 
        ? deals.reduce((sum, d) => sum + (d.value || 0), 0) / deals.length 
        : 0;

      const totalOrdersValue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
      
      const campaignBudget = campaigns.reduce((sum, c) => sum + (c.budget || 0), 0);
      const campaignSpent = campaigns.reduce((sum, c) => sum + (c.budget_spent || 0), 0);

      setCrmData({
        deals: {
          count: deals.length,
          pipeline_value: pipelineValue,
          won_value: wonDealsValue,
          avg_deal_size: avgDealSize,
          items: deals.slice(0, 20)
        },
        products: {
          count: products.length,
          items: products.slice(0, 20)
        },
        orders: {
          count: orders.length,
          total_value: totalOrdersValue,
          items: orders.slice(0, 20)
        },
        contacts: {
          count: contacts.length
        },
        campaigns: {
          count: campaigns.length,
          total_budget: campaignBudget,
          total_spent: campaignSpent,
          items: campaigns.slice(0, 20)
        }
      });
    } catch (error) {
      console.error('Failed to fetch CRM data:', error);
    }
  }, []);

  useEffect(() => {
    fetchBudget();
    fetchCrmData();
  }, [fetchBudget, fetchCrmData]);

  // Fetch budget versions
  const fetchVersions = async () => {
    if (!budgetId) return;
    try {
      setLoadingVersions(true);
      const res = await api.get(`/finance/budget-templates/${budgetId}/versions`);
      setVersions(res.data || []);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      toast.error('Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  // Create new version
  const createVersion = async () => {
    if (!budgetId) return;
    try {
      setSavingVersion(true);
      await api.post(`/finance/budget-templates/${budgetId}/versions`, {
        notes: newVersionNotes || 'Manual snapshot'
      });
      toast.success('Version saved successfully');
      setNewVersionNotes('');
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save version');
    } finally {
      setSavingVersion(false);
    }
  };

  // Restore version
  const restoreVersion = async (versionId) => {
    if (!budgetId || !versionId) return;
    try {
      await api.post(`/finance/budget-templates/${budgetId}/versions/${versionId}/restore`);
      toast.success('Budget restored to selected version');
      setShowVersionsDialog(false);
      fetchBudget();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore version');
    }
  };

  // Calculate row total
  const getRowTotal = (item) => {
    return MONTHS.reduce((sum, month) => {
      const key = `${fiscalYear}-${month.key}`;
      return sum + (parseFloat(item.monthly_amounts?.[key]) || 0);
    }, 0);
  };

  // Calculate column total
  const getColumnTotal = (monthKey) => {
    const key = `${fiscalYear}-${monthKey}`;
    return lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.monthly_amounts?.[key]) || 0);
    }, 0);
  };

  // Calculate grand total
  const getGrandTotal = () => {
    return lineItems.reduce((sum, item) => sum + getRowTotal(item), 0);
  };

  // Handle cell value change
  const handleCellChange = (rowIndex, monthKey, value) => {
    const key = `${fiscalYear}-${monthKey}`;
    const newItems = [...lineItems];
    if (!newItems[rowIndex].monthly_amounts) {
      newItems[rowIndex].monthly_amounts = {};
    }
    newItems[rowIndex].monthly_amounts[key] = parseFloat(value) || 0;
    setLineItems(newItems);
    setHasChanges(true);
  };

  // Handle cell edit start
  const handleCellClick = (rowIndex, monthKey) => {
    setEditingCell({ rowIndex, monthKey });
  };

  // Handle cell edit end
  const handleCellBlur = () => {
    setEditingCell(null);
  };

  // Handle key press in cell
  const handleCellKeyDown = (e, rowIndex, monthKey) => {
    if (e.key === 'Enter') {
      setEditingCell(null);
      // Move to next cell
      const monthIdx = MONTHS.findIndex(m => m.key === monthKey);
      if (monthIdx < 11) {
        setEditingCell({ rowIndex, monthKey: MONTHS[monthIdx + 1].key });
      } else if (rowIndex < lineItems.length - 1) {
        setEditingCell({ rowIndex: rowIndex + 1, monthKey: '01' });
      }
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const monthIdx = MONTHS.findIndex(m => m.key === monthKey);
      if (e.shiftKey) {
        if (monthIdx > 0) {
          setEditingCell({ rowIndex, monthKey: MONTHS[monthIdx - 1].key });
        } else if (rowIndex > 0) {
          setEditingCell({ rowIndex: rowIndex - 1, monthKey: '12' });
        }
      } else {
        if (monthIdx < 11) {
          setEditingCell({ rowIndex, monthKey: MONTHS[monthIdx + 1].key });
        } else if (rowIndex < lineItems.length - 1) {
          setEditingCell({ rowIndex: rowIndex + 1, monthKey: '01' });
        }
      }
    }
  };

  // Add new row
  const handleAddRow = () => {
    if (!newRow.category || !newRow.description) {
      toast.error('Please fill in category and description');
      return;
    }

    const row = {
      id: `row-${Date.now()}`,
      ...newRow,
      monthly_amounts: {}
    };

    setLineItems([...lineItems, row]);
    setShowAddRowDialog(false);
    setNewRow({
      category: '',
      subcategory: '',
      description: '',
      account_code: '',
      budget_type: 'opex',
      notes: ''
    });
    setHasChanges(true);
  };

  // Delete row
  const handleDeleteRow = (index) => {
    const newItems = lineItems.filter((_, i) => i !== index);
    setLineItems(newItems);
    setHasChanges(true);
  };

  // Duplicate row
  const handleDuplicateRow = (index) => {
    const item = lineItems[index];
    const newItem = {
      ...item,
      id: `row-${Date.now()}`,
      description: `${item.description} (Copy)`,
      monthly_amounts: { ...item.monthly_amounts }
    };
    const newItems = [...lineItems];
    newItems.splice(index + 1, 0, newItem);
    setLineItems(newItems);
    setHasChanges(true);
  };

  // Move row up/down
  const handleMoveRow = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === lineItems.length - 1) return;

    const newItems = [...lineItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setLineItems(newItems);
    setHasChanges(true);
  };

  // Apply CRM data to row
  const handleApplyCrmData = (type, value, rowIndex) => {
    const monthKey = '01'; // Apply to first month, user can adjust
    handleCellChange(rowIndex, monthKey, value);
    toast.success(`Applied ${type} data to row`);
    setShowCrmDataDialog(false);
  };

  // Fill row with equal monthly distribution
  const handleFillEqual = (rowIndex, annualAmount) => {
    const monthlyAmount = annualAmount / 12;
    const newItems = [...lineItems];
    MONTHS.forEach(month => {
      const key = `${fiscalYear}-${month.key}`;
      if (!newItems[rowIndex].monthly_amounts) {
        newItems[rowIndex].monthly_amounts = {};
      }
      newItems[rowIndex].monthly_amounts[key] = Math.round(monthlyAmount * 100) / 100;
    });
    setLineItems(newItems);
    setHasChanges(true);
  };

  // Save budget
  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/finance/budget-templates/${budgetId}`, {
        ...budget,
        line_items: lineItems
      });
      toast.success('Budget saved successfully');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save budget');
    } finally {
      setSaving(false);
    }
  };

  // Export to Excel
  const handleExport = async () => {
    try {
      const res = await api.get(`/finance/budget-templates/${budgetId}/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `budget_${budget?.name?.replace(/\s+/g, '_')}_${fiscalYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Budget exported to Excel');
    } catch (error) {
      toast.error('Failed to export budget');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getBudgetTypeStyle = (type) => {
    return BUDGET_TYPES.find(t => t.value === type)?.color || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="h-full flex flex-col" data-testid="budget-builder">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/finance')}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold">{budget?.name || 'Budget Builder'}</h1>
              <p className="text-sm text-gray-500">
                {budget?.template_type} • FY{fiscalYear}
                {hasChanges && <span className="text-amber-600 ml-2">• Unsaved changes</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { setShowVersionsDialog(true); fetchVersions(); }}>
              <History className="h-4 w-4 mr-2" />
              Version History
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowCrmDataDialog(true)}>
              <Database className="h-4 w-4 mr-2" />
              Pull CRM Data
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !hasChanges}>
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-gray-50 border-b px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button size="sm" variant="outline" onClick={() => setShowAddRowDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Row
            </Button>
            <div className="text-sm text-gray-500">
              {lineItems.length} line item{lineItems.length !== 1 ? 's' : ''}
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-500">Grand Total:</span>
            <span className="font-bold text-lg">{formatCurrency(getGrandTotal())}</span>
          </div>
        </div>

        {/* Spreadsheet */}
        <div className="flex-1 overflow-auto p-4">
          <div className="min-w-max">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 w-8"></th>
                  <th className="border p-2 text-left min-w-[150px]">Category</th>
                  <th className="border p-2 text-left min-w-[200px]">Description</th>
                  <th className="border p-2 text-center w-20">Type</th>
                  {MONTHS.map(month => (
                    <th key={month.key} className="border p-2 text-right w-24 bg-blue-50">
                      {month.label}
                    </th>
                  ))}
                  <th className="border p-2 text-right w-28 bg-green-50 font-bold">Total</th>
                  <th className="border p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, rowIndex) => (
                  <tr key={item.id} className="hover:bg-gray-50 group">
                    <td className="border p-1 text-center text-gray-400">
                      <GripVertical className="h-4 w-4 cursor-grab opacity-0 group-hover:opacity-100" />
                    </td>
                    <td className="border p-2">
                      <div className="font-medium">{item.category}</div>
                      {item.subcategory && (
                        <div className="text-xs text-gray-500">{item.subcategory}</div>
                      )}
                    </td>
                    <td className="border p-2">
                      <div>{item.description}</div>
                      {item.account_code && (
                        <div className="text-xs text-gray-400">#{item.account_code}</div>
                      )}
                    </td>
                    <td className="border p-2 text-center">
                      <Badge className={`text-xs ${getBudgetTypeStyle(item.budget_type)}`}>
                        {item.budget_type}
                      </Badge>
                    </td>
                    {MONTHS.map(month => {
                      const key = `${fiscalYear}-${month.key}`;
                      const value = item.monthly_amounts?.[key] || 0;
                      const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.monthKey === month.key;
                      
                      return (
                        <td 
                          key={month.key} 
                          className={`border p-0 text-right cursor-text ${isEditing ? 'bg-blue-50' : ''}`}
                          onClick={() => handleCellClick(rowIndex, month.key)}
                        >
                          {isEditing ? (
                            <input
                              ref={inputRef}
                              type="number"
                              className="w-full h-full p-2 text-right border-2 border-blue-500 outline-none"
                              defaultValue={value || ''}
                              autoFocus
                              onBlur={(e) => {
                                handleCellChange(rowIndex, month.key, e.target.value);
                                handleCellBlur();
                              }}
                              onKeyDown={(e) => handleCellKeyDown(e, rowIndex, month.key)}
                            />
                          ) : (
                            <div className="p-2 h-full">
                              {value ? formatCurrency(value).replace('$', '') : '-'}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="border p-2 text-right bg-green-50 font-medium">
                      {formatCurrency(getRowTotal(item))}
                    </td>
                    <td className="border p-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            const annual = prompt('Enter annual amount to distribute equally:');
                            if (annual) handleFillEqual(rowIndex, parseFloat(annual));
                          }}>
                            <Calculator className="h-4 w-4 mr-2" />
                            Fill Equal Monthly
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedRowIndex(rowIndex);
                            setShowCrmDataDialog(true);
                          }}>
                            <Link2 className="h-4 w-4 mr-2" />
                            Link CRM Data
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDuplicateRow(rowIndex)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate Row
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleMoveRow(rowIndex, 'up')} disabled={rowIndex === 0}>
                            <ArrowUp className="h-4 w-4 mr-2" />
                            Move Up
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleMoveRow(rowIndex, 'down')} disabled={rowIndex === lineItems.length - 1}>
                            <ArrowDown className="h-4 w-4 mr-2" />
                            Move Down
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDeleteRow(rowIndex)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Row
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {lineItems.length === 0 && (
                  <tr>
                    <td colSpan={16} className="border p-8 text-center text-gray-500">
                      <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No budget items yet</p>
                      <Button variant="outline" className="mt-3" onClick={() => setShowAddRowDialog(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add First Row
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
              {lineItems.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td className="border p-2"></td>
                    <td className="border p-2" colSpan={3}>TOTALS</td>
                    {MONTHS.map(month => (
                      <td key={month.key} className="border p-2 text-right bg-blue-100">
                        {formatCurrency(getColumnTotal(month.key))}
                      </td>
                    ))}
                    <td className="border p-2 text-right bg-green-100 text-lg">
                      {formatCurrency(getGrandTotal())}
                    </td>
                    <td className="border p-2"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Add Row Dialog */}
        <Dialog open={showAddRowDialog} onOpenChange={setShowAddRowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Budget Line Item</DialogTitle>
              <DialogDescription>
                Add a new row to your budget spreadsheet
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Input
                    value={newRow.category}
                    onChange={(e) => setNewRow({...newRow, category: e.target.value})}
                    placeholder="e.g., Marketing, Sales, HR"
                    data-testid="row-category-input"
                  />
                </div>
                <div>
                  <Label>Subcategory</Label>
                  <Input
                    value={newRow.subcategory}
                    onChange={(e) => setNewRow({...newRow, subcategory: e.target.value})}
                    placeholder="e.g., Advertising, Events"
                  />
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <Input
                  value={newRow.description}
                  onChange={(e) => setNewRow({...newRow, description: e.target.value})}
                  placeholder="e.g., Google Ads Campaign"
                  data-testid="row-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Account Code</Label>
                  <Input
                    value={newRow.account_code}
                    onChange={(e) => setNewRow({...newRow, account_code: e.target.value})}
                    placeholder="e.g., 6100"
                  />
                </div>
                <div>
                  <Label>Budget Type</Label>
                  <Select
                    value={newRow.budget_type}
                    onValueChange={(v) => setNewRow({...newRow, budget_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGET_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddRowDialog(false)}>Cancel</Button>
              <Button onClick={handleAddRow} disabled={!newRow.category || !newRow.description}>
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CRM Data Dialog */}
        <Dialog open={showCrmDataDialog} onOpenChange={setShowCrmDataDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-500" />
                Pull Data from CRM
              </DialogTitle>
              <DialogDescription>
                Import values from your CRM to populate budget cells
              </DialogDescription>
            </DialogHeader>
            
            {crmData ? (
              <div className="space-y-6 py-4">
                {/* Deals Section */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-500" />
                      Sales & Deals
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div 
                        className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                        onClick={() => selectedRowIndex !== null && handleApplyCrmData('Pipeline', crmData.deals.pipeline_value, selectedRowIndex)}
                      >
                        <p className="text-sm text-gray-500">Pipeline Value</p>
                        <p className="text-xl font-bold">{formatCurrency(crmData.deals.pipeline_value)}</p>
                      </div>
                      <div 
                        className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                        onClick={() => selectedRowIndex !== null && handleApplyCrmData('Won Deals', crmData.deals.won_value, selectedRowIndex)}
                      >
                        <p className="text-sm text-gray-500">Won Deals Value</p>
                        <p className="text-xl font-bold">{formatCurrency(crmData.deals.won_value)}</p>
                      </div>
                      <div 
                        className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                        onClick={() => selectedRowIndex !== null && handleApplyCrmData('Avg Deal', crmData.deals.avg_deal_size, selectedRowIndex)}
                      >
                        <p className="text-sm text-gray-500">Avg Deal Size</p>
                        <p className="text-xl font-bold">{formatCurrency(crmData.deals.avg_deal_size)}</p>
                      </div>
                      <div className="p-3 border rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-500">Total Deals</p>
                        <p className="text-xl font-bold">{crmData.deals.count}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Products Section */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Package className="h-4 w-4 text-purple-500" />
                      Products
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-gray-500 mb-2">{crmData.products.count} products available</div>
                    <div className="max-h-40 overflow-auto space-y-1">
                      {crmData.products.items.map(product => (
                        <div 
                          key={product.id}
                          className="flex items-center justify-between p-2 border rounded hover:bg-gray-50 cursor-pointer"
                          onClick={() => selectedRowIndex !== null && handleApplyCrmData('Product', product.price, selectedRowIndex)}
                        >
                          <span className="text-sm">{product.name}</span>
                          <span className="font-medium">{formatCurrency(product.price)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Orders Section */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4 text-orange-500" />
                      Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                      onClick={() => selectedRowIndex !== null && handleApplyCrmData('Orders', crmData.orders.total_value, selectedRowIndex)}
                    >
                      <p className="text-sm text-gray-500">Total Orders Value ({crmData.orders.count} orders)</p>
                      <p className="text-xl font-bold">{formatCurrency(crmData.orders.total_value)}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Marketing Campaigns */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Megaphone className="h-4 w-4 text-pink-500" />
                      Marketing Campaigns
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div 
                        className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                        onClick={() => selectedRowIndex !== null && handleApplyCrmData('Campaign Budget', crmData.campaigns.total_budget, selectedRowIndex)}
                      >
                        <p className="text-sm text-gray-500">Total Campaign Budget</p>
                        <p className="text-xl font-bold">{formatCurrency(crmData.campaigns.total_budget)}</p>
                      </div>
                      <div 
                        className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                        onClick={() => selectedRowIndex !== null && handleApplyCrmData('Campaign Spent', crmData.campaigns.total_spent, selectedRowIndex)}
                      >
                        <p className="text-sm text-gray-500">Total Spent</p>
                        <p className="text-xl font-bold">{formatCurrency(crmData.campaigns.total_spent)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Contacts */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-500" />
                      Contacts & Headcount
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-3 border rounded-lg bg-gray-50">
                      <p className="text-sm text-gray-500">Total Contacts</p>
                      <p className="text-xl font-bold">{crmData.contacts.count}</p>
                    </div>
                  </CardContent>
                </Card>

                {selectedRowIndex === null && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    <strong>Tip:</strong> Click on a row's menu and select "Link CRM Data" to apply values directly to that row.
                  </div>
                )}
              </div>
            ) : (
              <div className="py-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-2 text-gray-500">Loading CRM data...</p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowCrmDataDialog(false);
                setSelectedRowIndex(null);
              }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Version History Dialog */}
        <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Budget Version History
              </DialogTitle>
              <DialogDescription>
                View and restore previous versions of this budget
              </DialogDescription>
            </DialogHeader>

            {/* Create New Version */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Save Current Version</h4>
              <div className="flex gap-2">
                <Input
                  placeholder="Version notes (optional)"
                  value={newVersionNotes}
                  onChange={(e) => setNewVersionNotes(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={createVersion} disabled={savingVersion}>
                  {savingVersion ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Version
                </Button>
              </div>
            </div>

            {/* Version List */}
            <div className="space-y-3">
              <h4 className="font-medium">Version History</h4>
              {loadingVersions ? (
                <div className="py-8 text-center">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                </div>
              ) : versions.length > 0 ? (
                <div className="space-y-2">
                  {versions.map((version, idx) => (
                    <div 
                      key={version.id} 
                      className={`p-4 border rounded-lg ${idx === 0 ? 'border-green-300 bg-green-50' : ''}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">v{version.version_number}</Badge>
                            {idx === 0 && <Badge className="bg-green-100 text-green-700">Latest</Badge>}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{version.notes || 'No notes'}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(version.created_at).toLocaleString()}
                            {version.created_by_name && ` by ${version.created_by_name}`}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>{version.line_item_count || 0} line items</span>
                            <span>Total: {formatCurrency(version.total_budget || 0)}</span>
                          </div>
                        </div>
                        {idx > 0 && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => restoreVersion(version.id)}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <History className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p>No versions saved yet</p>
                  <p className="text-sm">Save a version to track changes over time</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVersionsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default BudgetBuilderPage;
